import React from 'react';
import { SlIcon } from '@shoelace-style/shoelace/dist/react';
import MediaThumb from './MediaThumb';
import StatusPill from './StatusPill';
import {
  canPick,
  canUpload,
  formatEpochDate,
  formatRelative,
  isVideoSubType,
  subTypeLabel,
} from '../config';

const th = 'px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500';
const td = 'px-4 py-3 align-middle text-sm text-slate-700';

/**
 * Desktop (md and up) table view. Rows are focusable and open with Enter.
 * The wrapper is the only horizontally scrollable element.
 */
const CreativeTable = ({ rows, onOpen, onPick, onUpload, busyId }) => (
  <div className="max-md:hidden overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
    <div className="overflow-x-auto">
      <table className="w-full min-w-[900px] border-collapse">
        <thead className="bg-slate-50">
          <tr className="border-b border-slate-200">
            <th className={th}>Creative</th>
            <th className={th}>Sub type</th>
            <th className={th}>Status</th>
            <th className={th}>Sales person</th>
            <th className={th}>Created</th>
            <th className={th}>Last activity</th>
            <th className={`${th} text-right`}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const video = isVideoSubType(row.sub_type);
            const busy = Number(busyId) === Number(row.bsc_id);
            return (
              <tr
                key={row.bsc_id}
                tabIndex={0}
                aria-label={`Open ${row.creative_name || `creative ${row.bsc_id}`}`}
                onClick={() => onOpen(row)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onOpen(row);
                  }
                }}
                className="cursor-pointer border-b border-slate-100 transition last:border-b-0 hover:bg-slate-50 focus:bg-sky-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-sky-400"
              >
                <td className={td}>
                  <div className="flex items-center gap-3">
                    <MediaThumb
                      src={row.img_urls}
                      isVideo={video && !!row.video_urls}
                      alt={row.creative_name || 'creative'}
                      className="h-11 w-11"
                    />
                    {/* NOT `truncate`: Styles.scss:3997 adds `max-width: 130px`
                        to that class, which clipped creative names to ~15
                        characters. `min-w-0` alone cannot bound an auto-layout
                        table column, so the cap is replaced with a deliberate,
                        readable one that still lets the ellipsis trigger. */}
                    <div className="min-w-0 max-w-[280px]">
                      <div
                        className="overflow-hidden text-ellipsis whitespace-nowrap font-medium text-slate-900"
                        title={row.creative_name}
                      >
                        {row.creative_name || `Creative #${row.bsc_id}`}
                      </div>
                      <div className="mt-0.5 flex items-center gap-2 text-xs text-slate-400">
                        <span>#{row.bsc_id}</span>
                        {Number(row.message_count) > 0 && (
                          <span className="inline-flex items-center gap-1 text-slate-500">
                            <SlIcon
                              library="lucide"
                              name="message-square"
                              style={{ fontSize: '12px' }}
                            />
                            {row.message_count}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </td>
                <td className={td}>
                  <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-slate-100 px-2 py-1 text-xs font-medium text-slate-700">
                    <SlIcon
                      library="lucide"
                      name={video ? 'video' : 'image'}
                      style={{ fontSize: '12px' }}
                    />
                    {subTypeLabel(row.sub_type)}
                  </span>
                </td>
                <td className={td}>
                  <StatusPill status={row.status} />
                </td>
                <td className={`${td} whitespace-nowrap`}>
                  {row.sales_person_name || (
                    <span className="text-slate-400">
                      {row.sales_person_id ? `#${row.sales_person_id}` : '—'}
                    </span>
                  )}
                </td>
                <td className={`${td} whitespace-nowrap text-slate-500`}>
                  {formatEpochDate(row.created_time)}
                </td>
                <td className={`${td} whitespace-nowrap text-slate-500`}>
                  {row.last_activity_time ? formatRelative(row.last_activity_time) : '—'}
                </td>
                <td className={`${td} text-right`}>
                  <div
                    className="flex items-center justify-end gap-2"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    role="presentation"
                  >
                    {canPick(row.status) && (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => onPick(row)}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-sky-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <SlIcon library="lucide" name="hand" style={{ fontSize: '13px' }} />
                        {busy ? 'Picking…' : 'Pick'}
                      </button>
                    )}
                    {canUpload(row.status) && (
                      <button
                        type="button"
                        onClick={() => onUpload(row)}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-sky-200 bg-sky-50 px-3 py-1.5 text-xs font-semibold text-sky-700 transition hover:bg-sky-100"
                      >
                        <SlIcon library="lucide" name="upload" style={{ fontSize: '13px' }} />
                        Upload
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => onOpen(row)}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-50"
                    >
                      Open
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
);

export default CreativeTable;
